<script setup lang="ts">
const props = defineProps({
  id: { type: Number, required: true },
  title: { type: String, required: true },
  text: { type: String, required: true },
});
</script>

<template>
  <section class="articleMain">
    <h2 :id="`chapter${('00' + id).slice(-2)}`">{{ title }}</h2>
    <div class="articleMain-inner" v-html="text" />
  </section>
</template>

<style lang="scss" scoped>
/*---------------------------------------
article
----------------------------------------*/
.articleMain {
  padding: 3em 0 0;

  h2 {
    padding: 2.4% 4% 2.4% 4%;
    border-top: 2px solid $colorContentsBorder;
    border-bottom: 1px solid #fff;
    font-size: px(18);
    font-weight: 700;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(244, 244, 244, 1) 100%
    );
    background-size: 100% 100%;
    box-shadow: 0px 1px 0px 0px #dcdcdc;
  }
}

.articleMain-inner :deep {
  h3 {
    margin: 2em 4% 0;
    padding: 0 0 0.4em 0.7em;
    border-bottom: 1px solid #dcdcdc;
    font-size: px(16.8);
    font-weight: 700;
    position: relative;
    line-height: 1.2;

    &::before {
      content: '';
      margin: 0 0.5em 0 0;
      width: 5px;
      height: 80%;
      background-color: $colorDarkBlue;
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  h4 {
    margin: 1em 4% -0.8em;
    font-weight: 700;
  }

  img.articleMain-img {
    padding: 1em 4% 0;
    box-sizing: border-box;
  }

  p {
    padding: 1em 4% 0;
    font-size: px(12.9);
    line-height: 1.77;

    a {
      text-decoration: underline;
    }
  }

  strong {
    &.red {
      color: #f00;
      font-weight: 700;
    }

    &.underLine {
      color: #333;
      font-weight: 700;
      background: linear-gradient(transparent 45%, #ff0 45%);
    }
  }

  .note {
    padding: 0.3em 4% 0;

    li {
      margin: 0 0 0 1em;
      color: #808080;
      font-size: px(12.9);
      text-indent: -1em;

      a {
        text-decoration: underline;
      }
    }
  }

  .tbl {
    padding: 2em 4% 0;
  }

  .tblCatch {
    margin: 0 0 0.3em;
    color: $colorDarkBlue;
    font-weight: 700;
  }

  .tbl {
    table {
      border: 1px solid #dcdcdc;
      width: 100%;
      box-sizing: border-box;
      table-layout: fixed;
    }

    th,
    td {
      padding: 0.5em 0.7em;
      font-size: px(12.9);
      background-color: #fff;
      line-height: 1.77;
      box-sizing: border-box;
    }

    th {
      font-weight: 700;
      text-align: left;

      a {
        font-weight: 700;
        text-decoration: underline;
      }
    }

    td a {
      text-decoration: underline;
    }

    thead th {
      border-right: 1px dotted #dcdcdc;
      background-color: $colorSubBackground;
    }

    tbody {
      th,
      td {
        border-right: 1px dotted #dcdcdc;
        border-bottom: 1px dotted #dcdcdc;
      }
    }

    table.scroll {
      width: auto;
      display: block;
      overflow-x: scroll;
      white-space: nowrap;
      -webkit-overflow-scrolling: touch;

      thead tr:first-child th:first-child,
      tbody th {
        position: -webkit-sticky;
        position: sticky;
        left: 0;
      }

      thead tr:first-child th:first-child::after,
      tbody th::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-right: 1px dotted #dcdcdc;
      }
    }

    td {
      li {
        margin: 0 0 0 1em;
        text-indent: -1em;
      }

      .ulList,
      .olList {
        padding: 0;
      }

      .ulList li,
      .olList li {
        margin: 0;
        text-indent: 0;
      }
    }

    .note {
      padding: 0.3em 0 0;
    }
  }

  /* .scroll */

  .ulList,
  .olList {
    padding: 1em 4% 0;
  }

  .listCatch {
    margin: 0 0 0.3em;
    color: $colorDarkBlue;
    font-weight: 700;
  }

  .ulList li,
  .olList li {
    padding: 0 0 0 1.7em;
    font-size: px(12.9);
    line-height: 1.77;
  }

  .ulList li {
    &:not(:last-of-type) {
      padding: 0 0 0.5em 1.7em;
    }

    background: url(~/assets/images/list_dot.png) no-repeat left 0.6em;
    background-size: 7px auto;
  }

  .olList li {
    margin: 0 0 0 1.7em;
    padding: 0;
    text-indent: -1.7em;

    &:not(:last-of-type) {
      margin: 0 0 0.5em 1.7em;
    }
  }

  .ulList li a,
  .olList li a {
    text-decoration: underline;
  }

  .point {
    margin: 1em 4% 0;
    padding: 4%;
    background: url(~/assets/images/bg_pattern_02.gif) no-repeat 0 0;
    background-size: 100% 100%;
    position: relative;

    &::before {
      content: '';
      width: 18%;
      height: 100%;
      background: url(~/assets/images/icon_point.png) no-repeat 0 0.1em;
      background-size: 100% auto;
      position: absolute;
    }
  }

  .pointTitle {
    margin: 0 0 0.5em;
    padding: 0 0 0 23%;
    color: $colorDarkBlue;
    font-weight: 700;
  }

  .point {
    > ul {
      padding: 9% 0 0;
    }

    .pointTitle + ul {
      padding: 0;
    }

    li {
      padding: 0 0 0 1.7em;
      font-size: px(12.9);
      font-weight: 700;
      background: url(~/assets/images/icon_check.png) no-repeat 0 0.3em;
      background-size: 1.3em auto;
      line-height: 1.7;

      &:not(:last-of-type) {
        margin: 0 0 0.3em;
      }

      a {
        font-weight: 700;
        text-decoration: underline;
      }
    }
  }
}
@include media(pc) {
  /*---------------------------------------
article
----------------------------------------*/
  .articleMain {
    padding: 60px 0 0;

    h2 {
      margin: 0 0 12px;
      padding: 10px 18px 10px;
      border-top: 3px solid $colorContentsBorder;
      border-bottom: 1px solid #fff;
      font-size: px(21.9);
    }
  }

  .articleMain-inner :deep {
    h3 {
      margin: 2em 0 0;
      padding: 0 0 0.4em 0.7em;
      font-size: px(21.9);
    }

    h4 {
      margin: 1em 0 -0.8em;
    }

    img.articleMain-img {
      padding: 1em 0 0;
    }

    p {
      padding: 1em 0 0;
      font-size: px(15);
      line-height: 1.66;
    }

    .tbl {
      padding: 2em 0 0;
    }

    .tblCatch {
      font-size: px(18.9);
    }

    .tbl {
      th,
      td {
        font-size: px(15);
        line-height: 1.66;
      }

      table.scroll {
        width: 100%;
        display: table;
        white-space: pre-wrap;

        thead tr:first-child th:first-child,
        tbody th {
          position: static;
        }

        thead tr:first-child th:first-child::after,
        tbody th::after {
          content: none;
        }
      }
    }

    /* .scroll */

    .note {
      padding: 0.3em 0 0;
    }

    .ulList,
    .olList {
      padding: 1em 0 0;
    }

    .listCatch {
      font-size: px(18.9);
    }

    .ulList li,
    .olList li {
      font-size: px(15);
      line-height: 1.66;
    }

    .point {
      margin: 1em 0 0;
      padding: 17px 19px;

      &::before {
        background: url(~/assets/images/icon_point.png) no-repeat 0 0.1em;
        background-size: 80px auto;
      }

      > ul {
        padding: 40px 0 0;
      }
    }

    .pointTitle {
      margin: 0 0 12px;
      padding: 0 0 0 90px;
      font-size: px(18.9);
    }

    .point li {
      padding: 0 0 0 37px;
      font-size: px(15);
    }
  }
}
</style>

